import { $api } from "@/lib/api";
import {
  Calendar,
  CheckSquare,
  Clock,
  MessageSquare,
  Users,
} from "lucide-react";

interface MeetingsTabProps {
  projectId: string;
}

export default function MeetingsTab({ projectId }: MeetingsTabProps) {
  const { data: meetings = [], isLoading } = $api.useQuery(
    "get",
    "/api/v1/meetings/by-project/{project_id}",
    { params: { path: { project_id: projectId } } },
    { staleTime: 5 * 60 * 1000, enabled: !!projectId },
  );

  if (isLoading) {
    return (
      <div className="p-6 space-y-4 animate-pulse">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="rounded-lg p-5"
            style={{ backgroundColor: "var(--surface)" }}
          >
            <div className="h-4 rounded w-1/2 mb-3" style={{ backgroundColor: "var(--bg)" }} />
            <div className="h-3 rounded w-full mb-2" style={{ backgroundColor: "var(--bg)" }} />
            <div className="h-3 rounded w-3/4" style={{ backgroundColor: "var(--bg)" }} />
          </div>
        ))}
      </div>
    );
  }

  if (meetings.length === 0) {
    return (
      <div className="p-6">
        <div className="empty-state">
          <Calendar className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--muted-foreground)" }} />
          <p className="text-sm mb-1" style={{ color: "var(--text-secondary)" }}>No meetings assigned to this project</p>
          <p className="text-xs" style={{ color: "var(--muted-foreground)" }}>
            Meetings are automatically assigned when the intelligence agent detects a match.
          </p>
        </div>
      </div>
    );
  }

  const formatDateTime = (dateStr: string | null | undefined) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return (
      d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
      " at " +
      d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    );
  };

  return (
    <div className="p-6 space-y-4 overflow-auto h-full">
      {meetings.map((meeting) => {
        const actionItemCount = meeting.action_items?.length ?? 0;
        const decisionCount = meeting.decisions?.length ?? 0;

        return (
          <div
            key={meeting.id}
            className="rounded-lg p-5"
            style={{
              backgroundColor: "var(--surface)",
              border: "1px solid var(--border)",
              borderLeft: meeting.enrichment_status === "summarized"
                ? "3px solid var(--accent)"
                : "3px solid var(--border)",
            }}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>
                  {meeting.subject || "Untitled meeting"}
                </h3>
                <div className="flex items-center gap-3 text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
                  {meeting.start_time && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDateTime(meeting.start_time)}
                    </span>
                  )}
                  {meeting.attendees && meeting.attendees.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {meeting.attendees.length}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Summary */}
            {meeting.summary && (
              <div className="mb-3 p-3 rounded" style={{ backgroundColor: "var(--secondary)", border: "1px solid var(--border)" }}>
                <p className="text-sm leading-relaxed line-clamp-4" style={{ color: "var(--text-primary)" }}>
                  {meeting.summary}
                </p>
              </div>
            )}

            {/* Key Topics */}
            {meeting.key_topics && meeting.key_topics.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {meeting.key_topics.map((topic, idx) => (
                  <span
                    key={idx}
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: "color-mix(in srgb, var(--info) 12%, transparent)", color: "var(--info)" }}
                  >
                    {topic}
                  </span>
                ))}
              </div>
            )}

            {/* Action items and decisions counts */}
            {(actionItemCount > 0 || decisionCount > 0) && (
              <div className="flex items-center gap-4 text-xs" style={{ color: "var(--text-secondary)" }}>
                {actionItemCount > 0 && (
                  <span className="flex items-center gap-1">
                    <CheckSquare className="w-3.5 h-3.5" />
                    {actionItemCount} action item{actionItemCount !== 1 ? "s" : ""}
                  </span>
                )}
                {decisionCount > 0 && (
                  <span className="flex items-center gap-1">
                    <MessageSquare className="w-3.5 h-3.5" />
                    {decisionCount} decision{decisionCount !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
